<template>
    <div class="layout-container" :class="containerClass">
        <AppTopbar :should-hide-navigation="shouldHideNavigation" :has-side-nav="hasSideNav" />
        <AppSidebar v-if="user && hasSideNav" :sidebar-component="sidebarComponent" />
        <div class="layout-content-wrapper" :class="{ 'tw:pt-0!': !isTopbarVisible }">
            <div class="layout-content" :class="{ 'tw:rounded-tl-none! tw:h-screen!': !isTopbarVisible }">
                <div class="layout-content-inner">
                    <AppBreadcrumb />
                    <router-view />
                    <AppFooter />
                </div>
            </div>
        </div>
        <Toast />
        <AppConfig />
        <portal-target name="dashboard-customization-sidebar" />

        <EdverumChat v-if="!shouldHideNavigation && check" />

        <NoteDrawer v-if="!shouldHideNavigation && hasNote" />

        <AlertDrawer v-if="user" />
    </div>
</template>

<script setup>
import get from 'lodash/get.js';
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import AlertDrawer from '@/components/alerts/AlertDrawer.vue';
import EdverumChat from '@/components/chat/EdverumChat.vue';
import NoteDrawer from '@/components/notes/NoteDrawer.vue';
import AppBreadcrumb from './AppBreadcrumb.vue';
import AppConfig from './AppConfig.vue';
import AppFooter from './AppFooter.vue';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
import { useLayout } from './composables/layout.js';

const { layoutConfig, layoutState, isSidebarActive, isTopbarVisible } = useLayout();

const outsideClickListener = ref(null);
const store = useStore();
const route = useRoute();

const props = defineProps({
    user: {
        type: Object,
        default: undefined,
    },
});

function isOutsideClicked(event) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.topbar-menubutton');

    return !(
        sidebarEl?.isSameNode(event.target) ||
        sidebarEl?.contains(event.target) ||
        topbarEl?.isSameNode(event.target) ||
        topbarEl?.contains(event.target)
    );
}

function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = event => {
            if (isOutsideClicked(event)) {
                layoutState.overlayMenuActive = false;
                layoutState.overlaySubmenuActive = false;
                layoutState.staticMenuMobileActive = false;
                layoutState.menuHoverActive = false;
                layoutState.configSidebarVisible = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
}

watch(isSidebarActive, newVal => {
    if (newVal) {
        bindOutsideClickListener();
    } else {
        unbindOutsideClickListener();
    }
});

watch(
    () => layoutState.staticMenuMobileActive,
    isActive => {
        if (window.innerWidth < 1200) {
            document.body.classList.toggle('blocked-scroll', isActive);
        }
    }
);

onBeforeUnmount(() => {
    unbindOutsideClickListener();
});

const containerClass = computed(() => {
    return [
        {
            'layout-light': layoutConfig.layoutTheme === 'colorScheme' && !layoutConfig.darkTheme,
            'layout-dark': layoutConfig.layoutTheme === 'colorScheme' && layoutConfig.darkTheme,
            'layout-primary': !layoutConfig.darkTheme && layoutConfig.layoutTheme === 'primaryColor',
            'layout-slim': layoutConfig.menuMode === 'slim',
            'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
            'layout-static': layoutConfig.menuMode === 'static',
            'layout-overlay': layoutConfig.menuMode === 'overlay',
            'layout-overlay-active': layoutState.overlayMenuActive,
            'layout-mobile-active': layoutState.staticMenuMobileActive,
            'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        },
    ];
});
const sidebarComponent = computed(() => {
    const sidebar = get(store.state.Menu, 'sidebar', /* backup: */ get(store.state, 'sidebar', 'app-sidebar'));

    if (route.path.startsWith('/courses/') || route.path.startsWith('/course-templates/')) {
        return 'app-sidebar';
    }

    return sidebar;
});

const hasActiveAttempt = computed(() => {
    return store.getters['User/hasActiveAttempt'] || route.name === 'automated-survey-attempt';
});

const check = computed(() => {
    return store.getters['Auth/check'];
});

const mustChangePassword = computed(() => {
    return props.user && props.user?.must_change_password;
});

const shouldHideNavigation = computed(() => {
    return hasActiveAttempt.value || mustChangePassword.value;
});

const instance = getCurrentInstance();
const $isMobile = computed(() => instance?.appContext.config.globalProperties.$isMobile);
const $isTablet = computed(() => instance?.appContext.config.globalProperties.$isTablet);

const hasSideNav = computed(() => {
    if (shouldHideNavigation.value) return false;

    if (route.name && route.name.includes('attempt')) return false;

    // Always show hamburger menu on mobile/tablet devices
    if ($isMobile.value || $isTablet.value) {
        return true;
    }

    if (
        sidebarComponent.value === 'user-sidebar' ||
        route.name === 'course-dashboard' ||
        route.name === 'course-template-view'
    ) {
        return true;
    }
    return store.getters['Menu/hasMenu'] && !store.state.Menu.hidden;
});

const hasNote = computed(() => {
    return store.state.NoteDrawer.showNoteDrawer;
});

watch(
    hasSideNav,
    newVal => {
        if (!newVal) {
            layoutState.staticMenuDesktopInactive = true;
        } else {
            layoutState.staticMenuDesktopInactive = false;
        }
    },
    { immediate: true }
);
</script>

<style lang="scss"></style>
